import React from 'react';
import { useSaaSContext } from '../../store/SaaSContext';
import { ShieldAlert, ShieldCheck, Eye, EyeOff, Scissors } from 'lucide-react';
import { formatUnit, parseUnit } from '../../utils/units';

interface SafeZonePanelProps {
  isModal?: boolean;
  onClose?: () => void;
}

export const SafeZonePanel: React.FC<SafeZonePanelProps> = ({ isModal, onClose }) => {
  const { layout, setLayout, template, setTemplate, appUnit } = useSaaSContext();

  const safeZone = template.safeZone !== undefined ? template.safeZone : (layout.safeZone !== undefined ? layout.safeZone : 8.5);
  const showSafeZone = template.showSafeZone !== false && layout.showSafeZone !== false;

  const handleUpdateSafeZone = (valInPt: number) => {
    const cleanVal = Math.max(0, valInPt);
    setTemplate(prev => ({ ...prev, safeZone: cleanVal, showSafeZone: true }));
    setLayout(prev => ({ ...prev, safeZone: cleanVal, showSafeZone: true }));
  };

  const handleToggleShow = (enabled: boolean) => {
    setTemplate(prev => ({ ...prev, showSafeZone: enabled }));
    setLayout(prev => ({ ...prev, showSafeZone: enabled }));
  };

  // Preset values in mm / pt
  const presets = [
    { label: '1.5 mm', pt: 4.25, desc: 'Tight bleed / small labels' },
    { label: '2.0 mm', pt: 5.67, desc: 'Standard sticker cut margin' },
    { label: '3.0 mm', pt: 8.5, desc: 'Commercial press recommendation' },
    { label: '5.0 mm', pt: 14.17, desc: 'Large cards & packaging' },
    { label: '1/8 in', pt: 9.0, desc: 'US standard print margin' },
  ];

  // Inspect template elements to see if any cross the safe zone
  const elementsOutsideSafeZone = template.elements.filter(el => {
    const elW = el.width || 40;
    const elH = el.height || 20;
    return (
      el.x < safeZone ||
      el.y < safeZone ||
      el.x + elW > template.width - safeZone ||
      el.y + elH > template.height - safeZone
    );
  });

  const fixElementsInsideSafeZone = () => {
    setTemplate(prev => {
      const updatedElements = prev.elements.map(el => {
        const elW = el.width || 40;
        const elH = el.height || 20;
        let newX = el.x;
        let newY = el.y;

        if (newX < safeZone) newX = safeZone;
        if (newY < safeZone) newY = safeZone;
        if (newX + elW > prev.width - safeZone) newX = Math.max(safeZone, prev.width - safeZone - elW);
        if (newY + elH > prev.height - safeZone) newY = Math.max(safeZone, prev.height - safeZone - elH);

        return { ...el, x: newX, y: newY };
      });
      return { ...prev, elements: updatedElements };
    });
  };

  return (
    <div className="bg-zinc-900 border border-zinc-700/80 rounded-xl p-4 text-zinc-100 shadow-2xl flex flex-col gap-4">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-zinc-800 pb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-emerald-600/20 text-emerald-400 rounded-lg border border-emerald-500/30">
            <ShieldCheck className="w-4 h-4" />
          </div>
          <div>
            <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
              Safe Zone (Inner Margin)
              <span className="text-[10px] bg-emerald-500/20 text-emerald-300 font-mono px-1.5 py-0.5 rounded border border-emerald-400/30">Visual Guide</span>
            </h3>
            <p className="text-[11px] text-zinc-400">Keep important text, barcodes, and artwork inside this margin to prevent cut-off</p>
          </div>
        </div>
        {isModal && onClose && (
          <button 
            onClick={onClose}
            className="text-zinc-400 hover:text-white px-2 py-1 rounded bg-zinc-800 text-xs font-semibold"
          >
            ✕
          </button>
        )}
      </div>

      {/* Main Toggle & Margin input */}
      <div className="bg-zinc-950 p-3 rounded-lg border border-zinc-800 flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2.5 cursor-pointer select-none">
            <input 
              type="checkbox"
              checked={showSafeZone}
              onChange={e => handleToggleShow(e.target.checked)}
              className="w-4 h-4 rounded border-zinc-700 accent-emerald-500 cursor-pointer"
            />
            <div className="flex flex-col">
              <span className="text-xs font-bold text-zinc-200 flex items-center gap-1.5">
                Enable Safe Zone Visual Guide
                {showSafeZone ? <Eye className="w-3.5 h-3.5 text-emerald-400" /> : <EyeOff className="w-3.5 h-3.5 text-zinc-500" />}
              </span>
              <span className="text-[10px] text-zinc-400">Shows dashed inner boundary & cut margin warning</span>
            </div>
          </label>
          <span className="text-xs font-mono font-bold text-emerald-400 bg-emerald-950/60 border border-emerald-800/60 px-2 py-0.5 rounded">
            {formatUnit(safeZone, appUnit)} {appUnit}
          </span>
        </div>

        {showSafeZone && (
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-800/80">
            <label className="text-xs font-medium text-zinc-400 whitespace-nowrap">Inner Margin:</label>
            <input 
              type="number"
              min="0"
              step="0.5"
              value={formatUnit(safeZone, appUnit)}
              onChange={e => handleUpdateSafeZone(parseUnit(e.target.value, appUnit))}
              className="flex-1 px-3 py-1.5 bg-zinc-900 border border-zinc-700 text-zinc-100 rounded text-xs font-mono outline-none focus:ring-2 focus:ring-emerald-500"
              placeholder={`e.g. 3 (${appUnit})`}
            />
            <span className="text-xs font-mono text-zinc-400">{appUnit}</span>
          </div>
        )}
      </div>

      {/* Quick Margin Presets */}
      <div className="flex flex-col gap-1.5">
        <label className="text-[11px] font-semibold text-zinc-300">Standard Margin Presets</label>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
          {presets.map(p => {
            const isSelected = Math.abs(safeZone - p.pt) < 0.2;
            return (
              <button
                key={p.label}
                onClick={() => handleUpdateSafeZone(p.pt)}
                className={`px-2.5 py-1.5 rounded-lg border text-xs font-medium text-left transition-all ${
                  isSelected
                    ? 'bg-emerald-600/30 border-emerald-500 text-emerald-200 shadow-sm'
                    : 'bg-zinc-950 border-zinc-800 text-zinc-300 hover:bg-zinc-800'
                }`}
              >
                <div className="font-bold text-[11px] text-zinc-100">{p.label}</div>
                <div className="text-[9px] text-zinc-400 truncate">{p.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Real-time Safety Check on Template Elements */}
      <div className={`p-3 rounded-lg border transition-all ${
        elementsOutsideSafeZone.length > 0 
          ? 'bg-amber-950/40 border-amber-800/70 text-amber-200'
          : 'bg-emerald-950/30 border-emerald-800/50 text-emerald-200'
      }`}>
        <div className="flex items-start gap-2.5">
          {elementsOutsideSafeZone.length > 0 ? (
            <ShieldAlert className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          ) : (
            <ShieldCheck className="w-5 h-5 text-emerald-400 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="text-xs font-bold flex items-center justify-between">
              <span>
                {elementsOutsideSafeZone.length > 0
                  ? `⚠️ ${elementsOutsideSafeZone.length} element(s) too close to the cut edge!`
                  : '✓ All elements are safely within the Safe Zone'
                }
              </span>
            </div>
            <p className="text-[10px] opacity-80 mt-0.5 leading-normal">
              {elementsOutsideSafeZone.length > 0
                ? 'Elements touching the margin risk being trimmed if the die-cutter shifts by 1–2mm.'
                : 'Your text and barcodes have sufficient clearance from the cutting blade.'
              }
            </p>

            {elementsOutsideSafeZone.length > 0 && (
              <button
                onClick={() => {
                  fixElementsInsideSafeZone();
                }}
                className="mt-2.5 px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs font-bold flex items-center gap-1.5 shadow-sm transition-colors cursor-pointer"
              >
                <Scissors className="w-3.5 h-3.5" />
                <span>Auto-Fit all elements inside Safe Zone</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Footer Action Buttons */}
      <div className="pt-2 border-t border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[10px] text-zinc-500">Live Visual Guide Active</span>
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className="px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold rounded-lg shadow-sm transition-colors cursor-pointer"
          >
            Apply & Close
          </button>
        )}
      </div>
    </div>
  );
};
